import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { brand } from '../brand'
import { mainNav, type NavItem } from './nav-data'
import {
    DesktopNav,
    Dropdown,
    HeaderBar,
    HeaderInner,
    LogoLink,
    MegaColumn,
    MegaColumnTitle,
    MegaFeatureLink,
    MegaLinkList,
    MegaMenu,
    MenuToggle,
    MobileNav,
    MobileNavItem,
    MobileNested,
    MobileOverlay,
    MobileSubNav,
    NavItem as NavItemEl,
    NavList,
    NavReveal,
} from './styles/Header.styled'

const MENU_CLOSE_MS = 700

const NavLabel = ({ label }: { label: string }) => (
    <NavReveal>
        <span className="nav-reveal-text" data-text={label}>
            {label}
        </span>
    </NavReveal>
)

const Header = () => {
    const [openMenu, setOpenMenu] = useState<string | null>(null)
    const [mobileOpen, setMobileOpen] = useState(false)
    const [mobileExpanded, setMobileExpanded] = useState<string | null>(null)
    const navRef = useRef<HTMLElement>(null)
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const unlockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const scrollLockRef = useRef<{ overflow: string; paddingRight: string } | null>(null)

    const clearCloseTimer = () => {
        if (closeTimerRef.current) {
            clearTimeout(closeTimerRef.current)
            closeTimerRef.current = null
        }
    }

    const openNavMenu = (label: string) => {
        clearCloseTimer()
        setOpenMenu(label)
    }

    const scheduleCloseNavMenu = () => {
        clearCloseTimer()
        closeTimerRef.current = setTimeout(() => {
            setOpenMenu(null)
            closeTimerRef.current = null
        }, 120)
    }

    const lockScroll = () => {
        if (scrollLockRef.current) return

        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
        scrollLockRef.current = {
            overflow: document.body.style.overflow,
            paddingRight: document.body.style.paddingRight,
        }
        document.body.style.overflow = 'hidden'
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`
            document.documentElement.style.setProperty(
                '--scrollbar-compensation',
                `${scrollbarWidth}px`
            )
        }
    }

    const unlockScroll = () => {
        if (!scrollLockRef.current) return
        document.body.style.overflow = scrollLockRef.current.overflow
        document.body.style.paddingRight = scrollLockRef.current.paddingRight
        document.documentElement.style.setProperty('--scrollbar-compensation', '0px')
        scrollLockRef.current = null
    }

    const openMobile = () => {
        if (unlockTimerRef.current) {
            clearTimeout(unlockTimerRef.current)
            unlockTimerRef.current = null
        }
        lockScroll()
        setMobileOpen(true)
    }

    const closeMobile = () => {
        setMobileOpen(false)
        setMobileExpanded(null)
        if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current)
        unlockTimerRef.current = setTimeout(() => {
            unlockScroll()
            unlockTimerRef.current = null
        }, MENU_CLOSE_MS)
    }

    useEffect(() => {
        const onPointerDown = (event: MouseEvent) => {
            if (!navRef.current?.contains(event.target as Node)) {
                clearCloseTimer()
                setOpenMenu(null)
            }
        }
        document.addEventListener('mousedown', onPointerDown)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            clearCloseTimer()
            if (unlockTimerRef.current) clearTimeout(unlockTimerRef.current)
            unlockScroll()
        }
    }, [])

    const renderMega = (item: NavItem) => {
        if (!item.columns) return null

        const isCompanies = item.label === 'Companies'

        return (
            <MegaMenu role="menu">
                {item.columns.map((column) => (
                    <MegaColumn key={column.label}>
                        {isCompanies && column.children?.[0] ? (
                            <MegaFeatureLink href={column.children[0].href}>
                                <span className="title">{column.children[0].label}</span>
                                {column.children[0].description && (
                                    <small className="desc">{column.children[0].description}</small>
                                )}
                            </MegaFeatureLink>
                        ) : (
                            <>
                                <MegaColumnTitle>{column.label}</MegaColumnTitle>
                                <MegaLinkList>
                                    {column.children?.map((link) => (
                                        <li key={link.href}>
                                            <a href={link.href}>{link.label}</a>
                                        </li>
                                    ))}
                                </MegaLinkList>
                            </>
                        )}
                    </MegaColumn>
                ))}
            </MegaMenu>
        )
    }

    return (
        <HeaderBar role="banner" aria-label="Main Menu">
            <HeaderInner>
                <LogoLink to="/" aria-label={brand.nameUpper}>
                    <img
                        src={brand.logos.header.src}
                        alt={brand.logos.header.alt}
                        height={brand.logos.header.height}
                    />
                </LogoLink>

                <DesktopNav ref={navRef} aria-label="Main Menu">
                    <NavList>
                        {mainNav.map((item) => {
                            const hasChildren = Boolean(item.children?.length || item.columns?.length)
                            const isOpen = openMenu === item.label

                            return (
                                <NavItemEl
                                    key={item.label}
                                    $mega={Boolean(item.mega)}
                                    data-open={isOpen || undefined}
                                    onMouseEnter={() => hasChildren && openNavMenu(item.label)}
                                    onMouseLeave={() => hasChildren && scheduleCloseNavMenu()}
                                >
                                    {hasChildren ? (
                                        <button
                                            type="button"
                                            aria-haspopup="true"
                                            aria-expanded={isOpen}
                                            onClick={() =>
                                                setOpenMenu(isOpen ? null : item.label)
                                            }
                                        >
                                            <NavLabel label={item.label} />
                                        </button>
                                    ) : (
                                        <Link to={item.href || '/'}>
                                            <NavLabel label={item.label} />
                                        </Link>
                                    )}

                                    {isOpen && item.children && !item.mega && (
                                        <Dropdown>
                                            {item.children.map((child) => (
                                                <li key={child.href}>
                                                    <a href={child.href}>{child.label}</a>
                                                </li>
                                            ))}
                                        </Dropdown>
                                    )}

                                    {isOpen && item.mega && renderMega(item)}
                                </NavItemEl>
                            )
                        })}
                    </NavList>
                </DesktopNav>

                <MenuToggle
                    type="button"
                    aria-label={mobileOpen ? 'Close Menu' : 'Navigation Menu'}
                    aria-expanded={mobileOpen}
                    $open={mobileOpen}
                    onClick={() => (mobileOpen ? closeMobile() : openMobile())}
                >
                    <span className="lines" aria-hidden="true">
                        <span />
                        <span />
                        <span />
                    </span>
                </MenuToggle>
            </HeaderInner>

            <MobileOverlay
                $open={mobileOpen}
                role="dialog"
                aria-modal="true"
                aria-hidden={!mobileOpen}
                aria-label="Off Canvas Menu"
            >
                <MobileNav>
                    {mainNav.map((item, index) => {
                        const hasChildren = Boolean(item.children?.length || item.columns?.length)
                        const expanded = mobileExpanded === item.label

                        return (
                            <MobileNavItem key={item.label} $open={mobileOpen} $index={index}>
                                {hasChildren ? (
                                    <button
                                        type="button"
                                        aria-expanded={expanded}
                                        tabIndex={mobileOpen ? 0 : -1}
                                        onClick={() =>
                                            setMobileExpanded(expanded ? null : item.label)
                                        }
                                    >
                                        {item.label}
                                    </button>
                                ) : (
                                    <Link
                                        to={item.href || '/'}
                                        tabIndex={mobileOpen ? 0 : -1}
                                        onClick={closeMobile}
                                    >
                                        {item.label}
                                    </Link>
                                )}

                                {expanded && item.children && !item.mega && (
                                    <MobileSubNav>
                                        {item.children.map((child) => (
                                            <li key={child.href}>
                                                <a
                                                    href={child.href}
                                                    tabIndex={mobileOpen ? 0 : -1}
                                                    onClick={closeMobile}
                                                >
                                                    {child.label}
                                                </a>
                                            </li>
                                        ))}
                                    </MobileSubNav>
                                )}

                                {expanded && item.columns && (
                                    <MobileNested>
                                        {item.columns.map((column) => (
                                            <div key={column.label}>
                                                {item.label === 'Companies' && column.children?.[0] ? (
                                                    <a
                                                        className="company-link"
                                                        href={column.children[0].href}
                                                        tabIndex={mobileOpen ? 0 : -1}
                                                        onClick={closeMobile}
                                                    >
                                                        {column.children[0].label}
                                                        {column.children[0].description && (
                                                            <small className="company-desc">
                                                                {column.children[0].description}
                                                            </small>
                                                        )}
                                                    </a>
                                                ) : (
                                                    <>
                                                        <span className="group-title">{column.label}</span>
                                                        <MobileSubNav>
                                                            {column.children?.map((child) => (
                                                                <li key={child.href}>
                                                                    <a
                                                                        href={child.href}
                                                                        tabIndex={mobileOpen ? 0 : -1}
                                                                        onClick={closeMobile}
                                                                    >
                                                                        {child.label}
                                                                    </a>
                                                                </li>
                                                            ))}
                                                        </MobileSubNav>
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </MobileNested>
                                )}
                            </MobileNavItem>
                        )
                    })}
                </MobileNav>
            </MobileOverlay>
        </HeaderBar>
    )
}

export default Header
